'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { Toaster, toast } from 'sonner';
import Icon from '@/components/ui/AppIcon';
import { createClient } from '@/lib/supabase/client';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

interface Cavo {
  id: string;
  etichetta: string;
  tipo: string;
  lunghezza_m: number;
  patch_panel_origine: string;
  porta_origine: string;
  switch_destinazione: string;
  porta_switch: string;
  presa_muro: string;
  percorso: string;
  sede_id: string | null;
  reparto_id: string | null;
  note: string;
  created_at: string;
}

const tipoColors: Record<string, string> = {
  cat5e: 'bg-gray-500/20 text-gray-400',
  cat6: 'bg-sky-500/20 text-sky-400',
  cat6a: 'bg-blue-500/20 text-blue-400',
  cat7: 'bg-violet-500/20 text-violet-400',
  fibra_sm: 'bg-amber-500/20 text-amber-400',
  fibra_mm: 'bg-orange-500/20 text-orange-400',
  dac: 'bg-green-500/20 text-green-400',
  altro: 'bg-gray-500/20 text-gray-400',
};

const emptyForm = { etichetta: '', tipo: 'cat6', lunghezza_m: 0, patch_panel_origine: '', porta_origine: '', switch_destinazione: '', porta_switch: '', presa_muro: '', percorso: '', note: '' };

export default function CablaggioContent() {
  const [cavi, setCavi] = useState<Cavo[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [tipoFilter, setTipoFilter] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingCavo, setEditingCavo] = useState<Cavo | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchCavi = useCallback(async () => {
    setLoading(true);
    try {
      const { data, error } = await supabase.from('cablaggio').select('*').order('etichetta');
      if (error) throw error;
      setCavi(data || []);
    } catch (err: any) {
      toast.error('Errore caricamento cablaggio: ' + err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchCavi(); }, [fetchCavi]);

  const filtered = cavi.filter(c => {
    const matchTipo = tipoFilter === 'all' || c.tipo === tipoFilter;
    const matchSearch = !search || c.etichetta.toLowerCase().includes(search.toLowerCase()) || c.patch_panel_origine.toLowerCase().includes(search.toLowerCase()) || c.percorso.toLowerCase().includes(search.toLowerCase());
    return matchTipo && matchSearch;
  });

  const openCreate = () => { setEditingCavo(null); setForm(emptyForm); setShowModal(true); };
  const openEdit = (c: Cavo) => {
    setEditingCavo(c);
    setForm({ etichetta: c.etichetta, tipo: c.tipo, lunghezza_m: c.lunghezza_m, patch_panel_origine: c.patch_panel_origine, porta_origine: c.porta_origine, switch_destinazione: c.switch_destinazione, porta_switch: c.porta_switch, presa_muro: c.presa_muro, percorso: c.percorso, note: c.note });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.etichetta) { toast.error('Etichetta obbligatoria'); return; }
    setSaving(true);
    try {
      if (editingCavo) {
        const { error } = await supabase.from('cablaggio').update(form).eq('id', editingCavo.id);
        if (error) throw error;
        toast.success('Cavo aggiornato');
      } else {
        const { error } = await supabase.from('cablaggio').insert(form);
        if (error) throw error;
        toast.success('Cavo aggiunto');
      }
      setShowModal(false);
      fetchCavi();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string, etichetta: string) => {
    if (!confirm(`Eliminare il cavo "${etichetta}"?`)) return;
    try {
      const { error } = await supabase.from('cablaggio').delete().eq('id', id);
      if (error) throw error;
      toast.success('Cavo eliminato');
      fetchCavi();
    } catch (err: any) {
      toast.error('Errore: ' + err.message);
    }
  };

  const totalLength = cavi.reduce((s, c) => s + c.lunghezza_m, 0);

  return (
    <div className="flex flex-col h-full bg-background">
      <Toaster position="bottom-right" theme="dark" richColors />

      <div className="px-6 py-5 border-b border-border bg-surface flex-shrink-0">
        <div className="flex items-center justify-between gap-4 flex-wrap">
          <div>
            <h1 className="text-2xl font-bold text-white">Gestione Cablaggio</h1>
            <p className="text-sm text-secondary-color mt-0.5">Patch panel, porte switch, prese a muro e percorsi cavo</p>
          </div>
          <button onClick={openCreate} className="flex items-center gap-2 px-4 py-2 bg-sky-500 text-white rounded-lg text-sm font-semibold hover:bg-sky-400 transition-all">
            <Icon name="PlusIcon" size={16} />
            Aggiungi Cavo
          </button>
        </div>

        <div className="grid grid-cols-3 gap-3 mt-4">
          {[
            { label: 'Cavi Totali', value: cavi.length, color: 'text-white' },
            { label: 'Lunghezza Totale', value: `${totalLength.toFixed(0)} m`, color: 'text-sky-400' },
            { label: 'Tipi Diversi', value: [...new Set(cavi.map(c => c.tipo))].length, color: 'text-violet-400' },
          ].map(k => (
            <div key={k.label} className="bg-surface-elevated border border-border rounded-lg px-4 py-3">
              <p className="text-xs font-bold text-muted-color uppercase tracking-wide">{k.label}</p>
              <p className={`text-2xl font-bold mt-0.5 ${k.color}`}>{k.value}</p>
            </div>
          ))}
        </div>
      </div>

      <div className="px-6 py-3 border-b border-border bg-surface flex items-center gap-3 flex-wrap">
        <div className="relative flex-1 min-w-48">
          <Icon name="MagnifyingGlassIcon" size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-color" />
          <input type="text" value={search} onChange={e => setSearch(e.target.value)} placeholder="Cerca etichetta, patch panel, percorso..." className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg pl-9 pr-3 py-2 focus:outline-none focus:border-sky-500/50" />
        </div>
        <div className="relative">
          <select value={tipoFilter} onChange={e => setTipoFilter(e.target.value)} className="bg-surface-elevated border border-border text-secondary-color text-sm font-semibold rounded-lg px-3 py-2 focus:outline-none appearance-none pr-7 cursor-pointer">
            <option value="all">Tutti i tipi</option>
            {Object.keys(tipoColors).map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
          </select>
          <Icon name="ChevronDownIcon" size={13} className="absolute right-2.5 top-1/2 -translate-y-1/2 text-muted-color pointer-events-none" />
        </div>
        <span className="text-sm text-muted-color font-semibold ml-auto">{filtered.length} cavi</span>
      </div>

      <div className="flex-1 overflow-auto scrollbar-thin">
        {loading ? (
          <div className="flex items-center justify-center h-64">
            <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : filtered.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-64 gap-3">
            <Icon name="LinkIcon" size={40} className="text-muted-color" />
            <p className="text-secondary-color font-semibold">Nessun cavo trovato</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-surface border-b border-border z-10">
              <tr>
                {['Etichetta', 'Tipo', 'Lunghezza', 'Patch Panel', 'Porta', 'Switch Dest.', 'Porta Switch', 'Presa Muro', 'Percorso', 'Azioni'].map(h => (
                  <th key={h} className="text-left px-4 py-3 text-xs font-bold text-muted-color uppercase tracking-wider whitespace-nowrap">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {filtered.map(c => (
                <tr key={c.id} className="hover:bg-surface-elevated/30 transition-colors">
                  <td className="px-4 py-3 font-mono text-white font-semibold">{c.etichetta}</td>
                  <td className="px-4 py-3">
                    <span className={`px-2 py-0.5 rounded text-xs font-bold ${tipoColors[c.tipo] || 'bg-gray-500/20 text-gray-400'}`}>{c.tipo.replace('_', ' ')}</span>
                  </td>
                  <td className="px-4 py-3 text-secondary-color">{c.lunghezza_m > 0 ? `${c.lunghezza_m}m` : '—'}</td>
                  <td className="px-4 py-3 font-mono text-secondary-color text-xs">{c.patch_panel_origine || '—'}</td>
                  <td className="px-4 py-3 font-mono text-secondary-color text-xs">{c.porta_origine || '—'}</td>
                  <td className="px-4 py-3 text-secondary-color text-xs">{c.switch_destinazione || '—'}</td>
                  <td className="px-4 py-3 font-mono text-secondary-color text-xs">{c.porta_switch || '—'}</td>
                  <td className="px-4 py-3 text-secondary-color text-xs">{c.presa_muro || '—'}</td>
                  <td className="px-4 py-3 text-secondary-color text-xs max-w-32 truncate">{c.percorso || '—'}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-1">
                      <button onClick={() => openEdit(c)} className="p-1.5 rounded-lg hover:bg-surface-elevated text-muted-color hover:text-white transition-colors">
                        <Icon name="PencilIcon" size={14} />
                      </button>
                      <button onClick={() => handleDelete(c.id, c.etichetta)} className="p-1.5 rounded-lg hover:bg-red-500/10 text-muted-color hover:text-red-400 transition-colors">
                        <Icon name="TrashIcon" size={14} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {showModal && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-2xl shadow-2xl max-h-[90vh] overflow-y-auto">
            <div className="flex items-center justify-between p-5 border-b border-border sticky top-0 bg-surface">
              <h2 className="text-lg font-bold text-white">{editingCavo ? 'Modifica Cavo' : 'Aggiungi Cavo'}</h2>
              <button onClick={() => setShowModal(false)} className="p-1.5 rounded-lg hover:bg-surface-elevated transition-colors">
                <Icon name="XMarkIcon" size={18} className="text-muted-color" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Etichetta *</label>
                  <input type="text" value={form.etichetta} onChange={e => setForm({ ...form, etichetta: e.target.value })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50" placeholder="es. CAB-001" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Tipo Cavo</label>
                  <select value={form.tipo} onChange={e => setForm({ ...form, tipo: e.target.value })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50">
                    {Object.keys(tipoColors).map(t => <option key={t} value={t}>{t.replace('_', ' ')}</option>)}
                  </select>
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-1.5">Lunghezza (m)</label>
                <input type="number" step="0.5" value={form.lunghezza_m} onChange={e => setForm({ ...form, lunghezza_m: parseFloat(e.target.value) || 0 })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Patch Panel Origine</label>
                  <input type="text" value={form.patch_panel_origine} onChange={e => setForm({ ...form, patch_panel_origine: e.target.value })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Porta Origine</label>
                  <input type="text" value={form.porta_origine} onChange={e => setForm({ ...form, porta_origine: e.target.value })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Switch Destinazione</label>
                  <input type="text" value={form.switch_destinazione} onChange={e => setForm({ ...form, switch_destinazione: e.target.value })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Porta Switch</label>
                  <input type="text" value={form.porta_switch} onChange={e => setForm({ ...form, porta_switch: e.target.value })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Presa a Muro</label>
                  <input type="text" value={form.presa_muro} onChange={e => setForm({ ...form, presa_muro: e.target.value })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50" />
                </div>
                <div>
                  <label className="block text-sm font-semibold text-secondary-color mb-1.5">Percorso Cavo</label>
                  <input type="text" value={form.percorso} onChange={e => setForm({ ...form, percorso: e.target.value })} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50" />
                </div>
              </div>
              <div>
                <label className="block text-sm font-semibold text-secondary-color mb-1.5">Note</label>
                <textarea value={form.note} onChange={e => setForm({ ...form, note: e.target.value })} rows={2} className="w-full bg-surface-elevated border border-border text-white text-sm rounded-lg px-3 py-2.5 focus:outline-none focus:border-sky-500/50 resize-none" />
              </div>
            </div>
            <div className="flex gap-3 p-5 border-t border-border sticky bottom-0 bg-surface">
              <button onClick={() => setShowModal(false)} className="flex-1 px-4 py-2.5 border border-border text-secondary-color rounded-lg text-sm font-semibold hover:bg-surface-elevated transition-all">Annulla</button>
              <button onClick={handleSave} disabled={saving} className="flex-1 px-4 py-2.5 bg-sky-500 text-white rounded-lg text-sm font-semibold hover:bg-sky-400 transition-all disabled:opacity-50">
                {saving ? 'Salvataggio...' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
